import { useRef, type ClipboardEvent, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";

export interface OtpInputProps {
  /** The full code so far (parent-owned); shorter than `length` while being typed. */
  value: string;
  onChange: (value: string) => void;
  /** Number of digit boxes. */
  length?: number;
  /** Red error styling on every box. */
  invalid?: boolean;
  /** Focus the first box on mount. */
  autoFocus?: boolean;
  /** Group label for assistive tech (each box is labelled "<ariaLabel> N"). */
  ariaLabel?: string;
  /** Fired once all `length` digits are present (e.g. to auto-submit). */
  onComplete?: (value: string) => void;
}

/**
 * Segmented one-time-code input — one box per digit, matching the design system's `.code-row`
 * (see KitPage "Search & 2FA"). Numeric only, with the OTP affordances users expect: auto-advance
 * on entry, backspace to the previous box, arrow-key navigation, and paste of a whole code spread
 * across the boxes. The parent owns the joined string; this component is presentation + caret logic.
 */
export function OtpInput({
  value,
  onChange,
  length = 6,
  invalid,
  autoFocus,
  ariaLabel,
  onComplete,
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? "");

  function focusBox(i: number) {
    const el = refs.current[Math.max(0, Math.min(i, length - 1))];
    el?.focus();
    el?.select();
  }

  /** Write `raw` (one or more digits, e.g. from autofill) starting at box `i`, then advance + emit. */
  function write(i: number, raw: string) {
    const incoming = raw.replace(/\D/g, "");
    if (!incoming) return;
    const next = digits.slice();
    let pos = i;
    for (const c of incoming) {
      if (pos >= length) break;
      next[pos] = c;
      pos += 1;
    }
    const joined = next.join("").slice(0, length);
    onChange(joined);
    focusBox(pos);
    if (joined.length === length) onComplete?.(joined);
  }

  function handleKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = digits.slice();
      if (digits[i]) {
        next[i] = "";
        onChange(next.join("").slice(0, length));
      } else if (i > 0) {
        next[i - 1] = "";
        onChange(next.join("").slice(0, length));
        focusBox(i - 1);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusBox(i + 1);
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    focusBox(text.length);
    if (text.length === length) onComplete?.(text);
  }

  return (
    <div className="code-row" role="group" aria-label={ariaLabel}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className={cn("input code-box", invalid && "input-error")}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={d}
          autoFocus={autoFocus && i === 0}
          aria-label={`${ariaLabel ?? "Digit"} ${i + 1}`}
          aria-invalid={invalid || undefined}
          onChange={(e) => write(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}
